// Appmainbar.jsx
import React, { useState, useEffect } from "react";
import { styled, useTheme } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import {
  Box,
  Collapse,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import {
  getSidebarItemLabel,
  readCollapsedGroupIds,
  sidebarGroups,
  sidebarHomeItem,
  writeCollapsedGroupIds,
} from "./sidebarMenu";

const drawerWidth = 260;

const labelWrapSx = (open) => ({
  whiteSpace: open ? "normal" : "nowrap",
  overflowWrap: "anywhere",
});

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  transitionDelay: `0.2s`,
  overflowX: "hidden",
  ".MuiListItemText-root": {
    opacity: 1,
    transitionDelay: `0.2s`,
  },
  ".sidebar-group-chevron": {
    opacity: 1,
    transitionDelay: `0.2s`,
  },
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  ".MuiListItemText-root": {
    opacity: 0,
  },
  ".sidebar-group-chevron": {
    opacity: 0,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== !["open"].includes(String(prop)),
})(({ theme, open, hover }) => ({
  width: drawerWidth,
  height: `100%`,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
    "&:hover ": {
      ...openedMixin(theme),
      "& .MuiDrawer-paper": openedMixin(theme),
    },
  }),
}));

function SidebarLink({ item, decoded, pathname, open, nested, t, language }) {
  if (!item.isVisible(decoded)) return null;

  const Icon = item.Icon;

  return (
    <ListItemButton
      component={Link}
      to={item.to}
      selected={item.isSelected(pathname)}
      sx={{
        alignItems: "flex-start",
        ...(nested && open ? { pl: 4 } : null),
        "& .MuiListItemIcon-root": {
          mt: "2px",
        },
        "& .MuiListItemText-root": {
          my: 0.25,
        },
      }}
    >
      <ListItemIcon>
        <Icon />
      </ListItemIcon>
      <ListItemText
        primary={getSidebarItemLabel(item, t, language)}
        primaryTypographyProps={{
          sx: labelWrapSx(open),
        }}
      />
    </ListItemButton>
  );
}

function Appmainbar({ isPinned, decoded }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const userId = decoded?.user_id;
  const [collapsedIds, setCollapsedIds] = useState(() =>
    readCollapsedGroupIds(userId)
  );

  const handleDrawerOpen = () => {
    if (!isPinned) setOpen(true);
  };

  const handleDrawerClose = () => {
    if (!isPinned) setOpen(false);
  };

  useEffect(() => {
    if (isPinned) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [isPinned]);

  useEffect(() => {
    setCollapsedIds(readCollapsedGroupIds(userId));
  }, [userId]);

  const toggleGroup = (groupId) => {
    setCollapsedIds((current) => {
      const next = current.includes(groupId)
        ? current.filter((id) => id !== groupId)
        : [...current, groupId];
      writeCollapsedGroupIds(userId, next);
      return next;
    });
  };

  const isRtl = theme.direction === "rtl";

  return (
    <Drawer
      variant="permanent"
      open={open}
      onMouseEnter={handleDrawerOpen}
      onMouseLeave={handleDrawerClose}
    >
      <List
        sx={{
          marginTop: "64px",
          color: (theme) => theme.palette.text.primary,
          "& .MuiListItemText-primary": {
            color: (theme) => theme.palette.text.primary,
          },
        }}
      >
        <SidebarLink
          item={sidebarHomeItem}
          decoded={decoded}
          pathname={location.pathname}
          open={open}
          t={t}
          language={i18n.language}
        />
        {sidebarGroups.map((group) => {
          const visibleItems = group.items.filter((item) =>
            item.isVisible(decoded)
          );
          if (visibleItems.length === 0) return null;

          const expanded = !collapsedIds.includes(group.id);
          const GroupIcon = group.Icon;

          return (
            <React.Fragment key={group.id}>
              <ListItemButton
                onClick={() => toggleGroup(group.id)}
                aria-expanded={expanded}
                aria-controls={`sidebar-group-${group.id}`}
                sx={{
                  alignItems: "flex-start",
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(0,0,0,0.35)"
                      : "rgba(0,0,0,0.06)",
                  "&:hover": {
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(0,0,0,0.5)"
                        : "rgba(0,0,0,0.10)",
                  },
                  "& .MuiListItemIcon-root": {
                    mt: "2px",
                  },
                  "& .MuiListItemText-root": {
                    my: 0.25,
                  },
                }}
              >
                <ListItemIcon>
                  <GroupIcon />
                </ListItemIcon>
                <ListItemText
                  primary={t(group.titleKey)}
                  primaryTypographyProps={{
                    sx: {
                      ...labelWrapSx(open),
                      fontWeight: 600,
                    },
                  }}
                />
                <Box
                  className="sidebar-group-chevron"
                  sx={{ ml: "auto", display: "flex", mt: "2px" }}
                >
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      transition: (theme) =>
                        theme.transitions.create("transform", {
                          duration: theme.transitions.duration.shorter,
                        }),
                      transform: expanded
                        ? "rotate(0deg)"
                        : isRtl
                          ? "rotate(90deg)"
                          : "rotate(-90deg)",
                    }}
                  />
                </Box>
              </ListItemButton>
              <Collapse
                in={expanded}
                timeout="auto"
                unmountOnExit
                id={`sidebar-group-${group.id}`}
              >
                <List component="div" disablePadding>
                  {visibleItems.map((item) => (
                    <SidebarLink
                      key={item.id}
                      item={item}
                      decoded={decoded}
                      pathname={location.pathname}
                      open={open}
                      nested
                      t={t}
                      language={i18n.language}
                    />
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          );
        })}
      </List>
    </Drawer>
  );
}

export default Appmainbar;
